import { createBrowserHistory, type ParsedPath, type RouterHistory } from '@tanstack/react-router';
import { omit } from 'radashi';

declare module '@tanstack/react-router' {
  interface HistoryState extends CustomHistoryState {}
}

export interface CustomHistoryState {
  /**
   * A stack of possible return locations is automatically maintained by the custom history
   */
  returnLocationStack?: { normalizedPath: string; location: ParsedPath }[];

  /**
   * The return location can be explicitly set when pushing a new location. If set to true, the return location will be automatically determined as the previous location. If set to a location, it will be used as the return location.
   */
  returnLocation?: boolean | string | ParsedPath;
}

export default function createCustomHistory(options?: Parameters<typeof createBrowserHistory>[0]): RouterHistory {
  const history = createBrowserHistory(options);
  const originalPush = history.push;
  const originalReplace = history.replace;

  history.push = (location, state, opts) => {
    originalPush.call(history, location, updateState('push', location, state), opts);
  };

  history.replace = (location, state, opts) => {
    originalReplace.call(history, location, updateState('replace', location, state), opts);
  };

  function updateState(
    type: 'push' | 'replace',
    location: string,
    state: CustomHistoryState | undefined,
  ): CustomHistoryState {
    let returnLocationStack = [
      ...(history.location.state?.returnLocationStack ?? []),
      ...(state?.returnLocationStack ?? []),
    ];
    let returnLocation = state?.returnLocation;

    const newPath = normalizePath(location);

    if (type === 'push') {
      const oldPath = normalizePath(history.location.pathname);

      returnLocationStack.push({
        normalizedPath: oldPath,
        location: omit(history.location, ['state']),
      });

      if (returnLocation === true) {
        returnLocation = omit(history.location, ['state']);
      } else if (typeof returnLocation === 'string') {
        const url = new URL(returnLocation, window.location.href);
        returnLocation = {
          href: url.href,
          pathname: url.pathname,
          search: url.search,
          hash: url.hash,
        };
      }
    }

    const index = returnLocationStack.findIndex((entry) => entry.normalizedPath.startsWith(newPath));
    if (index >= 0) {
      returnLocationStack = returnLocationStack.slice(0, index);
    }

    if (type === 'replace') {
      returnLocation ??= history.location.state?.returnLocation;
    }

    return {
      ...state,
      returnLocationStack,
      returnLocation,
    };
  }

  Object.assign(window, { _history: history });
  return history;
}

export function normalizePath(path: string) {
  path = new URL(path, window.location.href).pathname;

  if (!path.endsWith('/')) {
    path += '/';
  }

  return path;
}
